import { useState } from 'react';
import {
  Card, CardContent, Grid, Stack, Button, TextField, MenuItem, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar, Box, LinearProgress,
} from '@mui/material';
import { Add, TrackChanges, AccountBalanceWallet } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { dealerInsightsApi } from '../../api/dealerInsights.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const MONTHS = ['Annual', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const now = new Date().getFullYear();
const YEARS = [now - 1, now, now + 1];

/**
 * INSIGHTS → Targets + Analytics. Business Heads set dealer-wise monthly/annual
 * targets; the table shows target vs achievement (achieved = actual order value).
 */
export function DealerTargetsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('dealer_insight.manage');
  const [year, setYear] = useState(now);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState({ dealerId: '', periodMonth: 0, targetValue: 0 });

  const ownership = useQuery({ queryKey: ['dealer-ownership'], queryFn: () => dealerInsightsApi.ownership() });
  const analytics = useQuery({ queryKey: ['dealer-analytics', year], queryFn: () => dealerInsightsApi.analytics(year) });

  const createMut = useMutation({
    mutationFn: () => dealerInsightsApi.createTarget({ dealerId: Number(form.dealerId), periodYear: year, periodMonth: form.periodMonth === 0 ? null : form.periodMonth, targetValue: Number(form.targetValue) }),
    onSuccess: () => { setOpen(false); setForm({ dealerId: '', periodMonth: 0, targetValue: 0 }); setError(null); setToast('Target set'); qc.invalidateQueries({ queryKey: ['dealer-analytics'] }); },
    onError: (e) => setError(extractApiError(e)),
  });

  const t = analytics.data?.totals;
  return (
    <>
      <PageHeader title="Dealer Targets & Achievement" subtitle="Dealer-wise monthly/annual targets vs actual order value."
        action={canManage ? <Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>Set Target</Button> : undefined} />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 120 }}>
          {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={4}><Metric icon={<TrackChanges />} label="Total Target" value={inr(t?.target)} /></Grid>
        <Grid item xs={6} md={4}><Metric icon={<AccountBalanceWallet />} label="Achieved" value={inr(t?.achieved)} /></Grid>
        <Grid item xs={6} md={4}><Metric icon={<TrackChanges />} label="Achievement" value={`${t?.achievementPct ?? 0}%`} /></Grid>
      </Grid>

      <Card variant="outlined"><CardContent>
        {analytics.isLoading && <LinearProgress />}
        <Table size="small">
          <TableHead><TableRow><TableCell>Dealer</TableCell><TableCell>Owner</TableCell><TableCell align="right">Target</TableCell><TableCell align="right">Achieved</TableCell><TableCell align="right">Achievement</TableCell></TableRow></TableHead>
          <TableBody>
            {(analytics.data?.rows ?? []).map((r) => (
              <TableRow key={r.dealerId} hover>
                <TableCell><Typography fontWeight={600}>{r.dealerName}</Typography><Typography variant="caption" color="text.secondary">{r.dealerCode}</Typography></TableCell>
                <TableCell>{r.ownerName ?? '—'}</TableCell>
                <TableCell align="right">{inr(r.target)}</TableCell>
                <TableCell align="right">{inr(r.achieved)}</TableCell>
                <TableCell align="right"><Chip size="small" color={r.achievementPct >= 100 ? 'success' : r.achievementPct >= 60 ? 'warning' : 'default'} label={`${r.achievementPct}%`} /></TableCell>
              </TableRow>
            ))}
            {(analytics.data?.rows.length ?? 0) === 0 && <TableRow><TableCell colSpan={5}><Box py={2}><Typography variant="body2" color="text.secondary" textAlign="center">No dealers / targets for {year}.</Typography></Box></TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Set Dealer Target — {year}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Stack spacing={1.5} mt={1}>
            <TextField select label="Dealer" value={form.dealerId} onChange={(e) => setForm((f) => ({ ...f, dealerId: e.target.value }))} fullWidth required>
              {(ownership.data?.dealers ?? []).map((d) => <MenuItem key={d.id} value={d.id}>{d.dealerName} ({d.dealerCode})</MenuItem>)}
            </TextField>
            <TextField select label="Period" value={form.periodMonth} onChange={(e) => setForm((f) => ({ ...f, periodMonth: Number(e.target.value) }))} fullWidth>
              {MONTHS.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Target Value" type="number" value={form.targetValue} onChange={(e) => setForm((f) => ({ ...f, targetValue: Number(e.target.value) }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.dealerId || !form.targetValue || createMut.isPending} onClick={() => createMut.mutate()}>Set Target</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast(null)}><Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <Card sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', color: '#fff', borderRadius: 2, boxShadow: '0 6px 18px rgba(30,58,138,0.18)' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}><Typography variant="h6" fontWeight={800} lineHeight={1.15} noWrap>{value}</Typography><Typography variant="caption" sx={{ opacity: 0.85 }}>{label}</Typography></Box>
      </CardContent>
    </Card>
  );
}
